import { HttpClientModule } from "@angular/common/http";
import { APP_INITIALIZER, Injectable, Injector, NgModule } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { AConfigurationsFacade, AUserPermissionsService, ConfigurationService, CoreModule, EnvironmentService, PageHeaderModule } from "@coso-replicator/core";
import { ConnectionFormsModule, CONNECTION_FORMS_MODULE_CONFIG, Dynamics365ConnectionHandlerService, SharePointConnectionHandlerService, EntraIdConnectionHandlerService } from "@coso/connection-forms";
import { EnvironmenterModule } from "@coso/environmenter";
import { LayoutAngularModule } from "@coso/layout-angular";
import { LoadingModule } from "@coso/loading";
import { LoggerModule } from "@coso/logger";
import { ATokenActivationConfigurationProvider, AuthModule, CREATE_NEW_CONFIGURATION_URL, SaasAngularModule, CosoScheduledDowntime } from "@coso/saas-angular";
import { DEPLOY_URL, RuntimeSettingsModule, RuntimeSettingsService, UtilityModule } from "@coso/utility";
import { MonacoEditorModule, MONACO_PATH } from "@materia-ui/ngx-monaco-editor";
import { ModalModule } from "ngx-bootstrap/modal";
import { PaginationModule } from "ngx-bootstrap/pagination";
import { TabsModule } from "ngx-bootstrap/tabs";
import { TooltipModule } from "ngx-bootstrap/tooltip";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "../environments/environment";
import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { ReleaseNotesComponent } from "./release-notes/release-notes.component";
import { ConfigurationsFacade } from "./services/configurations.facade";
import { UserPermissionsService } from "./services/user-permissions.service";
import { FormsModule as CosoFormsModule } from "@coso/forms";

const monacoPathFactory = (injector: Injector) => {
  const deployUrl = injector.get(DEPLOY_URL);
  return deployUrl + "assets/monaco-editor/min/vs";
};

const connectionFormsConfigurationFactory = (configSvc: RuntimeSettingsService, environmentSvc: EnvironmentService) => {
  return {
    sharepoint: {
      modernAuthentication: {
        applicationPermissions: "Sites.FullControl.All or Sites.Selected",
        delegatedPermissions: "AllSites.FullControl or Sites.Selected"
      }
    },
    saasConfiguration: {
      managementServiceWebApi: configSvc.configObject.managementServiceWebApiEndpoint,
      useAuthorizeEndpoint: true,
      clientId: environmentSvc.configClientId      
    }
  };
};

// @note maybe makes sense to move S2S:D365SharePointApplicationId to UiSettings
export function initConfig(environmentSvc: EnvironmentService) {
  // we need to have declaration and return split because of angular compiler https://stackoverflow.com/questions/51976671/app-initializer-in-library-causes-lambda-not-supported-error
  const x = () => environmentSvc.loadConfigClientId();
  return x;
}

@Injectable()
export class TokenActivationConfigurationProvider extends ATokenActivationConfigurationProvider {
  constructor(private configurationService: ConfigurationService) {
    super();
  }

  getConfigurations(): Observable<{ id: string; name: string }[]> {
    return this.configurationService.get().pipe(map((configurations) => configurations.items.map((c) => ({ id: c.id, name: c.name }))));
  }
}

@NgModule({
  declarations: [AppComponent, ReleaseNotesComponent],
  imports: [
    BrowserModule,
    AppRoutingModule,
    LoadingModule,
    HttpClientModule,
    BrowserAnimationsModule,
    PaginationModule,
    TooltipModule,
    EnvironmenterModule.forRoot(environment),
    LoggerModule.toConsole(),
    MonacoEditorModule,
    LayoutAngularModule,
    RuntimeSettingsModule.forRoot({ configurationUrl: "/api/uisettings" }),
    AuthModule,
    ConnectionFormsModule,
    CoreModule.forRoot(true),
    SaasAngularModule,
    PageHeaderModule,
    UtilityModule,
    ModalModule,
    TabsModule,
    CosoFormsModule,
    CosoScheduledDowntime
  ],
  bootstrap: [AppComponent],
  providers: [
    {
      provide: MONACO_PATH,
      useFactory: monacoPathFactory,
      deps: [Injector]
    },
    {
      provide: CONNECTION_FORMS_MODULE_CONFIG,
      useFactory: connectionFormsConfigurationFactory,
      deps: [RuntimeSettingsService, EnvironmentService]
    },
    {
      provide: APP_INITIALIZER,
      useFactory: initConfig,
      deps: [EnvironmentService],
      multi: true
    },
    SharePointConnectionHandlerService,
    Dynamics365ConnectionHandlerService,
    EntraIdConnectionHandlerService,
    {
      provide: CREATE_NEW_CONFIGURATION_URL,
      useValue: "/configuration/create"
    },
    {
      provide: ATokenActivationConfigurationProvider,
      useClass: TokenActivationConfigurationProvider
    },
    {
      provide: AUserPermissionsService,
      useClass: UserPermissionsService
    },
    {
      provide: AConfigurationsFacade,
      useClass: ConfigurationsFacade
    }
  ]
})
export class AppModule {}
